import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import {
  Asset,
  AssetCreate,
  AssetOut,
  AssetUpdate,
  BatchAssetCreate,
  ClientOut,
  DeviceLicenseCreate,
  DeviceLicenseOut,
  License,
  PermissionCreate,
  PermissionListOut,
  PermissionOut,
  PermissionRuleCreate,
  PermissionRuleOut,
  PermissionRuleUpdate,
  PermissionUpdate,
  ProductCreate,
  ProductDetailsOut,
  ProductFamily,
  ProductOut,
  ProductUpdate,
  SessionCreate,
  SessionOut,
  SubscriptionLicenseCreate,
  TemplateCreate,
  TemplateOut,
  TemplateUpdate,
  UserCreate,
  UserOut,
  UserUpdate,
  DataPage,
} from './types';
import { router } from '@/router';
import { sessionStore } from '@/stores/Session';


export interface IApiClient {
  getAll(): Promise<any[]>;
  getById(id: string): Promise<any>;
  getByPage(page: number, size: number): Promise<DataPage<any>>;
  create(data: any): Promise<any>;
  update(id: string, data: any): Promise<any>;
  delete(id: string): Promise<void>;
}

/**
 * @class ApiClient
 * @implements {IApiClient}
 * @classdesc Generic API client for performing CRUD operations.
 */
class ApiClient implements IApiClient {
  protected readonly apiClient: AxiosInstance;
  protected urlSuffix: string;

  /**
    * Constructor for the class.
    * Initializes the API client with default configurations,
    * including a timeout of 10000ms and setting the 'Content-Type' header to 'application/json'.
    */
  constructor() {
    this.urlSuffix = "/";
    this.apiClient = axios.create({
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.apiClient.defaults.withCredentials = true;

    this.apiClient.interceptors.response.use(
      response => response,
      error => {
        if (error.response.status === 401 && error.config.url !== '/login') {
          sessionStore().clearSession();
          router.push({ name: 'login' });
        }
        return Promise.reject(error);
      }
    );
    this.apiClient.interceptors.request.use(
        config => {
            const session = sessionStore();
            session.loadSession();
            const token = session.session?.token;
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        error => Promise.reject(error),
    );

    if (process.env.NODE_ENV === 'development')
      this.apiClient.defaults.baseURL = process.env.VUE_APP_DEV_API_URL;
    else if (process.env.NODE_ENV === 'test')
      this.apiClient.defaults.baseURL = process.env.VUE_APP_TEST_API_URL;
    else if (process.env.NODE_ENV === 'production') 
      this.apiClient.defaults.baseURL = process.env.VUE_APP_PROD_API_URL;
    else
      this.apiClient.defaults.baseURL = "/";
  }

  /**
   * Retrieves all items from the API endpoint.
   *
   * @returns {Promise<any[]>} A promise that resolves to an array of items.
   */
  async getAll(): Promise<any[]> {
    const response: AxiosResponse<any[]> = await this.apiClient.get(this.urlSuffix);
    return response.data;
  }

  /**
   * Retrieves a resource by its ID.
   *
   * @param id The ID of the resource to retrieve.
   * @returns A promise that resolves with the resource data.
   */
  async getById(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.apiClient.get(`${this.urlSuffix}${id}`);
    return response.data;
  }

  /**
   * Retrieves a page of data from the API.
   *
   * @param page The page number to retrieve (1-indexed).
   * @param pageSize The number of items per page.
   * @returns A promise that resolves to a `DataPage` containing the data.
   */
  async getByPage(page: number, pageSize = 100): Promise<DataPage<any>> {
    const response: AxiosResponse<any> = await this.apiClient.get(`${this.urlSuffix}?page=${page}&size=${pageSize}`);
    return response.data as unknown as DataPage<any>;
  }

  /**
   * Creates a new resource using the API.
   *
   * @param data - The data for creating the resource.
   * @returns A promise that resolves with the created resource data.
   */
  async create(data: any): Promise<any> {
    const response: AxiosResponse<any> = await this.apiClient.post(this.urlSuffix, data);
    return response.data;
  }

  /**
   * Updates an existing resource.
   *
   * @param id The ID of the resource to update.
   * @param data The data to update the resource with.
   * @returns A promise that resolves with the updated resource.
   */
  async update(id: string, data: any): Promise<any> {
    const response: AxiosResponse<any> = await this.apiClient.put(`${this.urlSuffix}${id}`, data);
    return response.data;
  }

  /**
   * Deletes a resource by its ID.
   *
   * @param id The ID of the resource to delete.
   * @returns A promise that resolves when the resource is successfully deleted.
   */
  async delete(id: string): Promise<void> {
    await this.apiClient.delete(`${this.urlSuffix}${id}`);
  }

  getUrl(): string {
    return this.apiClient.defaults.baseURL + this.urlSuffix;
  }

  getBaseUrl(): string {
    return this.apiClient.defaults.baseURL;
  }

  /**
   * Searches for items using a search string query parameter.
   * 
   * @param searchString - The search query string to filter results
   * @returns A promise that resolves to a DataPage containing the search results
   * @throws May throw an error if the API request fails
   * 
   * @example
   * ```typescript
   * const results = await searchByString("example query");
   * ```
   */
  async searchByString(searchString: string): Promise<DataPage<any>> {
    const response: AxiosResponse<any[]> = await this.apiClient.get(
      `${this.urlSuffix}search/?q=${encodeURIComponent(searchString)}`
    );
    return response.data as unknown as DataPage<any>;
  }
}

export class LicenseApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/license/";
  }

  /**
   * Updates a device license with the given ID.
   *
   * @param id The ID of the device license to update.
   * @param data The data to update the device license with.
   * @returns A promise that resolves with the updated asset.
   */
  async updateDeviceLicense(id: string, data: DeviceLicenseCreate): Promise<AssetOut> {
    const response: AxiosResponse<AssetOut> = await this.apiClient.put(`${this.urlSuffix}device/${id}`, data);
    return response.data;
  }

  /**
   * Retrieves a license by its serial number.
   *
   * @param serialNumber The serial number of the license to retrieve.
   * @returns A promise that resolves to the device license data.
   */
  async getLicenseBySerialNumber(serialNumber: string): Promise<DeviceLicenseOut> {
    const response: AxiosResponse<DeviceLicenseOut> = await this.apiClient.get(`${this.urlSuffix}${serialNumber}`);
    return response.data;
  }
  
  /**
   * Retrieves the license history for a given serial number.
   *
   * @param serialNumber The serial number to retrieve the license history for.
   * @returns A promise that resolves to an asset with license history.
   */
  async getLicenseHistory(serialNumber: string): Promise<AssetOut> {
    const response: AxiosResponse<AssetOut> = await this.apiClient.get(`${this.urlSuffix}${serialNumber}/history/`);
    return response.data;
  }

  /**
   * Retrieves licenses for a specific client.
   *
   * @param clientId The ID of the client.
   * @returns A promise that resolves to an array of assets.
   */
  async getClientLicenses(clientId: string): Promise<AssetOut[]> {
    const response: AxiosResponse<AssetOut[]> = await this.apiClient.get(`${this.urlSuffix}client/${clientId}/licenses/`);
    return response.data;
  }

  /**
   * Updates a subscription license with the given ID.
   *
   * @param id The ID of the subscription license to update.
   * @param data The data to update the subscription license with.
   * @returns A promise that resolves with the updated asset.
   */
  async updateSubscriptionLicense(id: string, data: SubscriptionLicenseCreate): Promise<AssetOut> {
    const response: AxiosResponse<AssetOut> = await this.apiClient.put(`${this.urlSuffix}subscription/${id}`, data);
    return response.data;
  }

  
  /**
   * Retrieves the root password for an asset device.
   * 
   * @param serialNumber - The serial number of the asset device
   * @returns A promise that resolves to the root password as a string
   * @throws {AxiosError} If the API request fails or the device is not found
   */
  async getAssetRootPassword(serialNumber: string): Promise<string> {
    const response: AxiosResponse<string> = await this.apiClient.post(`${this.urlSuffix}device/webpw/`, { serial_number: serialNumber });
    return response.data;
  }


  /**
   *  Retrieves the AGOS 4 root password for an asset device using its serial number.
   * 
   * @param serialNumber - The serial number of the asset device
   * @returns A promise that resolves to the legacy root password as a string
   * @throws {AxiosError} If the API request fails or the device is not found
   */
  async getLegacyAssetRootPassword(serialNumber: string): Promise<string> {
    const response: AxiosResponse<string> = await this.apiClient.get(`${this.getBaseUrl()}/api/v1/passgen/api/password/${serialNumber}`);
    return response.data;
  }

  /**
   * Clears the hardware information for an asset device using its ID.
   * 
   * @param id - The ID of the asset device
   * @returns A promise that resolves to a string indicating the result of the operation
   * @throws {AxiosError} If the API request fails or the device is not found
   */
  async clearAssetHwInfo(id: string): Promise<string> {
    const response: AxiosResponse<string> = await this.apiClient.delete(`${this.getBaseUrl()}/api/v1/license/hwInfo/${id}`);
    return response.data;
  }

  /**
   * Uploads a hardware information file for an asset device using its serial number.
   *
   * @param serialNumber - The serial number of the asset device
   * @param file - The file to be uploaded
   * @param importHwInfo - A boolean flag indicating whether to import the hardware information after upload (default: false)
   * @returns A promise that resolves to a string indicating the result of the file upload operation
   * @throws {AxiosError} If the API request fails or the device is not found
   */
  async uploadHwInfoFile(serialNumber: string, file: File, importHwInfo = false): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    let url = `${this.getBaseUrl()}/api/v1/license/upload-hardware-info/${serialNumber}`;
    if (importHwInfo) {
      url += '/import'; // Append '/import' to the URL if import_hwinfo is true
    }
    const response: AxiosResponse<string> = await this.apiClient.post(`${url}`, formData, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
    return response.data;
  }
}

export class ProductApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/product/";
  }

  /**
   * Retrieves a product by its ID with full details.
   *
   * @param id The ID of the product to retrieve.
   * @returns A promise that resolves with the product details.
   */
  async getById(id: string): Promise<ProductDetailsOut> {
    const response: AxiosResponse<ProductDetailsOut> = await this.apiClient.get(`${this.urlSuffix}${id}`);
    return response.data;
  }

  /**
   * Retrieves a list of active products from the API.
   *
   * @returns {Promise<ProductOut[]>} A promise that resolves to an array of active products.
   * @throws {AxiosError} If the HTTP request fails.
   */
  async getActiveProducts(): Promise<ProductOut[]> {
    const response: AxiosResponse<ProductOut[]> = await this.apiClient.get(`${this.urlSuffix}active`);
    return response.data;
  }

  /**
   * Retrieves a list of products by their family prefix from the API.
   *
   * @param familyPrefix - The prefix of the product family to filter by.
   * @returns {Promise<ProductOut[]>} A promise that resolves to an array of products matching the family prefix.
   * @throws {AxiosError} If the HTTP request fails.
   */
  async getProductsByFamily(familyPrefix: string): Promise<ProductOut[]> {
    const response: AxiosResponse<ProductOut[]> = await this.apiClient.get(`${this.urlSuffix}family/${familyPrefix}`);
    return response.data;
  }
}
export class UserApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/user/";
  }

  /**
   * Authenticates a user by sending a login request with the provided token and email.
   *
   * @param token - The authentication token to be used for login.
   * @param email - The email address of the user attempting to log in.
   * @returns A promise that resolves to a `SessionOut` object containing session information.
   */
  async login (token: string, email: string): Promise<SessionOut> {
    const response: AxiosResponse<SessionOut> = await this.apiClient.post(`${this.urlSuffix}login/`, { token, email });
    return response.data;
  }

  /**
   * Retrieves the current user's session information from the server.
   *
   * @returns {Promise<SessionOut>} A promise that resolves to the user's session data.
   * @throws {AxiosError} If the request fails due to a network or server error.
   */
  async getUserSession(): Promise<SessionOut> {
    const response: AxiosResponse<SessionOut> = await this.apiClient.get(`${this.urlSuffix}session/`);
    return response.data;
  }

  /**
   * Logs out the current user by sending a DELETE request to the logout endpoint.
   *
   * @returns A promise that resolves when the logout operation is complete.
   */
  async logout(): Promise<void> {
    await this.apiClient.delete(`${this.urlSuffix}logout/`);
  }
}
export class PermissionApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/permission/";
  }

  /**
   * Retrieves the list of currently active permissions from the API.
   *
   * @returns {Promise<PermissionListOut[]>} A promise that resolves to an array of active permissions.
   * @throws {AxiosError} If the request fails.
   */
  async getActivePermissions(): Promise<PermissionListOut[]> {
    const response: AxiosResponse<PermissionListOut[]> = await this.apiClient.get(`${this.urlSuffix}active`);
    return response.data;
  }
}
export class PermissionRuleApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/permission-rule/";
  }
}
export class TemplateApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/license/template/";
  }
}
export class ClientApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/client/";
  }
}

export class ProductFamilyApiClient extends ApiClient implements IApiClient {

  constructor() {
    super();
    this.urlSuffix = "/api/v2/product/families/";
  }
  
  /**
   * Retrieves a paginated list of product families.
   * 
   * @param page - The page number to retrieve (zero-based or one-based depending on implementation)
   * @param pageSize - The number of items to include per page
   * @returns A promise that resolves to a DataPage containing ProductFamily items with normalized IDs
   * 
   * @remarks
   * This method calls the parent class's getByPage method and then normalizes the data
   * by mapping each item's `_id` property to an `id` property.
   */
  async getByPage(page: number, pageSize: number): Promise<DataPage<ProductFamily>> {
    const data: DataPage<any> = await super.getByPage(page, pageSize);
    data.items.map((item) => {
      item.id = item._id;
      return item;
    });
    return data;
  }
}

export class AssetBatchApiClient extends ApiClient implements IApiClient {
  constructor() {
    super();
    this.urlSuffix = "/api/v2/license/batch/";
  }

  /**
   * Downloads a batch report as a CSV file.
   * 
   * Fetches the batch report from the API as a blob, creates a temporary download link,
   * and triggers a download in the browser. The file is automatically named with the
   * batch ID and current timestamp.
   * 
   * @param batchId - The unique identifier of the batch to download the report for
   * @returns A promise that resolves when the download has been triggered
   * @throws {AxiosError} If the API request fails
   */
  async downloadBatchReport(batchId: string): Promise<void> {
    const response: AxiosResponse<Blob> = await this.apiClient.get(`${this.urlSuffix}${batchId}/report/`, {
      responseType: 'blob',
    });
    const ts = new Date().getTime();
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `batch_${batchId}_report_${ts}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export enum ApiStatus {
  IDLE,
  LOADING,
  NOT_FOUND,
  ERROR, 
}